<template>
    <button
        v-qa="currency"
        :aria-selected="selected"
        :class="{ selected }"
        class="currency-option focusable"
        role="option"
        type="button"
        @click="emit('select', currency)"
        @keydown.enter.prevent.stop="emit('select', currency)"
    >
        <span class="primary">
            {{ currency }}
            {{ getCurrencySymbol(currency) }}
        </span>

        <span class="label">{{ getCurrencyName(currency) }}</span>

        <Icon
            v-if="selected"
            class="checkmark"
            icon="checkmark"
        />
    </button>
</template>

<script lang="ts" setup>
import Icon from '@/components/ui/icon/Icon.vue'
import type { AppCurrency } from '@/plugins/currency-types'
import { useTranslator } from '@/i18n'

const { getCurrencyName, getCurrencySymbol } = useTranslator()
const { currency, selected } = defineProps<{
    currency: AppCurrency
    selected: boolean
}>()

const emit = defineEmits<{
    select: [currency: AppCurrency]
}>()
</script>

<style lang="scss" scoped>
.currency-option {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 10px;
    width: 100%;
    min-height: 44px;
    padding: 10px 12px;
    border: 1px solid transparent;
    border-radius: var(--border-radius-m, 8px);
    background: transparent;
    text-align: left;
    font-family: $effra-font-stack;
    color: $slate;
    cursor: pointer;
    @include default-transition();

    @include min-width(1024) {
        min-height: 48px;
    }

    &.focusable {
        &:focus {
            outline: none;
        }

        &:focus-visible {
            border-color: $blue;
            box-shadow: 0 0 0 1px $blue;
        }

        @include hover() {
            background: $stone-2;
        }
    }

    &.selected {
        background: $stone-2;
        border-color: $grey-300;
    }

    .primary {
        flex-shrink: 0;
        font-size: 0.875rem;
        line-height: 1.3;
        white-space: nowrap;

        @include min-width(1024) {
            font-size: 0.9375rem;
        }
    }

    .label {
        flex: 1 1 auto;
        min-width: 0;
        font-size: 0.75rem;
        line-height: 1.3;
        color: $grey-500;
        text-align: right;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .checkmark {
        flex-shrink: 0;
        font-size: 12px;
        color: $blue;
    }
}
</style>
